import { AnalyticsEventType } from '../../model/analytics.model';
import { trackEvent } from './analytics';

export interface YoutubeAnalyticsData {
  videoId?: string;
  layout?: string;
  title?: string;
  width?: number;
  height?: number;
  aspectRatio?: number;
  autoPlay?: boolean;
  loop?: boolean;
  mute?: boolean;
  playInline?: boolean;
  rel?: boolean;
}

export enum VideoEventType {
  READY = 'ready',
  PLAY = 'play',
  PAUSE = 'pause',
  COMPLETE = 'complete',
  MILESTONE_REACHED = 'milestoneReached',
  QUALITY_CHANGE = 'qualityChange',
  ERROR = 'error',
}

const getYoutubeEventData = (data: YoutubeAnalyticsData, player: YT.Player) => {
  const videoData = player.getVideoData();

  return {
    ...data,
    ...window.digitalData?.page.pageInfo,
    title: videoData.title ?? data.title ?? `YouTube Video ${data.videoId}`,
    provider: 'youtube',
    timestamp: Date.now(),
    duration: player.getDuration(),
    currentTime: player.getCurrentTime(),
    playbackRate: player.getPlaybackRate(),
    playbackQuality: player.getPlaybackQuality(),
  };
};

export const trackYoutubeVideoReady = (data: YoutubeAnalyticsData, player: YT.Player) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    eventType: VideoEventType.READY,
    ...getYoutubeEventData(data, player),
  });
};

export const trackYoutubeVideoPlay = (data: YoutubeAnalyticsData, player: YT.Player) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    eventType: VideoEventType.PLAY,
    ...getYoutubeEventData(data, player),
  });
};

export const trackYoutubeVideoPause = (data: YoutubeAnalyticsData, player: YT.Player) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    eventType: VideoEventType.PAUSE,
    ...getYoutubeEventData(data, player),
  });
};

export const trackYoutubeVideoComplete = (data: YoutubeAnalyticsData, player: YT.Player) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    eventType: VideoEventType.COMPLETE,
    ...getYoutubeEventData(data, player),
  });
};

export const trackYoutubeVideoMilestoneReached = (data: YoutubeAnalyticsData, percent: number, player: YT.Player) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    type: VideoEventType.MILESTONE_REACHED,
    milestone: percent,
    ...getYoutubeEventData(data, player),
  });
};

export const trackYoutubeVideoPlaybackQualityChange = (
  data: YoutubeAnalyticsData,
  newQuality: string,
  player: YT.Player
) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    type: VideoEventType.QUALITY_CHANGE,
    newQuality,
    ...getYoutubeEventData(data, player),
  });
};

const errorMessages: Record<YT.PlayerError, string> = {
  2: 'Invalid video ID',
  5: 'Video cannot be played in HTML5 player',
  100: 'Video not found or private',
  101: 'Video does not allow embedded playback',
  150: 'Video does not allow embedded playback',
};

export const trackYoutubeVideoError = (data: YoutubeAnalyticsData, errorCode: YT.PlayerError, player: YT.Player) => {
  trackEvent(AnalyticsEventType.YOUTUBE_VIDEO_EVENT, {
    type: VideoEventType.ERROR,
    errorCode,
    errorMessage: errorMessages[errorCode] ?? 'Unknown error',
    ...getYoutubeEventData(data, player),
  });
};
